import React, { useState } from 'react';
import { useResume } from '../context/ResumeContext';
import { useWorkspaceContext } from '../../../pages/ChatPage/providers/WorkspaceProvider';
import { getRegistryItem } from '../registry';
import moveItem from '../utils/moveItem';
import duplicateItem from '../utils/duplicateItem';
import deleteItem from '../utils/deleteItem';
import { Plus, Trash2, Copy, ArrowUp, ArrowDown, ChevronRight, ChevronDown } from 'lucide-react';
import FieldFactory from './FieldFactory';
import VaultAttachPanel from '../components/VaultAttachPanel';

// Sections where vault attachment is meaningful
const VAULT_SUPPORTED = new Set(['certifications', 'awards', 'publications', 'achievements']);

export default function GenericListSection({ sectionId }) {
  const { activeResume, updateField } = useResume();
  const { authToken, theme } = useWorkspaceContext();
  const meta = getRegistryItem(sectionId) || {};
  const list = activeResume?.[sectionId] || [];
  const showVault = VAULT_SUPPORTED.has(sectionId);

  const [expandedIndex, setExpandedIndex] = useState(0);

  const handleFieldChange = (idx, fieldName, val) => {
    updateField(`${sectionId}.${idx}.${fieldName}`, val);
  };

  const handleAddItem = () => {
    const defaultItem = JSON.parse(JSON.stringify(meta.defaultData || {}));
    defaultItem.id = `${sectionId}_${Date.now()}`;
    const nextList = [...list, defaultItem];
    updateField(sectionId, nextList);
    setExpandedIndex(nextList.length - 1);
  };

  const handleMove = (idx, direction) => {
    const nextList = moveItem(list, idx, direction);
    updateField(sectionId, nextList);
    setExpandedIndex(direction === 'up' ? idx - 1 : idx + 1);
  };

  const handleDuplicate = (idx) => {
    const nextList = duplicateItem(list, idx);
    if (nextList[idx + 1]) {
      nextList[idx + 1].id = `${sectionId}_${Date.now()}`;
    }
    updateField(sectionId, nextList);
    setExpandedIndex(idx + 1);
  };

  const handleDelete = (idx) => {
    const nextList = deleteItem(list, idx);
    updateField(sectionId, nextList);
    setExpandedIndex(Math.max(0, idx - 1));
  };

  if (list.length === 0) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '24px 16px', textAlign: 'center',
        border: '1px dashed var(--border-default)', borderRadius: 12,
        background: 'var(--surface-1)'
      }}>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px' }}>
          {meta.emptyState || 'No entries added yet.'}
        </p>
        <button
          onClick={handleAddItem}
          style={{
            padding: '8px 16px', fontSize: 11.5, fontWeight: 700,
            background: 'var(--brand)', color: 'white', border: 'none',
            borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          <Plus size={13} /> {`Add ${meta.title || 'Entry'}`}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {list.map((item, idx) => {
        const isExpanded = expandedIndex === idx;
        
        // Find a main display value for the card header (e.g. name or title field)
        const primaryFieldName = meta.fields?.[0]?.name || 'name';
        const displayLabel = item[primaryFieldName] || `${meta.title || 'Entry'} #${idx + 1}`;

        return (
          <div
            key={idx}
            style={{
              border: '1px solid var(--card-border)',
              borderRadius: 10,
              background: 'var(--surface-1)',
              overflow: 'hidden'
            }}
          >
            {/* Item Card Header */}
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', background: isExpanded ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
                cursor: 'pointer', userSelect: 'none'
              }}
              onClick={() => setExpandedIndex(isExpanded ? -1 : idx)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                {isExpanded ? <ChevronDown size={14} color="var(--text-muted)" /> : <ChevronRight size={14} color="var(--text-muted)" />}
                <span style={{
                  fontSize: 12, fontWeight: 700, color: 'var(--text-primary)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                  {displayLabel}
                </span>
              </div>

              {/* Item Card Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => handleMove(idx, 'up')}
                  disabled={idx === 0}
                  style={{
                    background: 'transparent', border: 'none', color: 'var(--text-muted)',
                    cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.3 : 0.8
                  }}
                >
                  <ArrowUp size={12} />
                </button>
                <button
                  onClick={() => handleMove(idx, 'down')}
                  disabled={idx === list.length - 1}
                  style={{
                    background: 'transparent', border: 'none', color: 'var(--text-muted)',
                    cursor: idx === list.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === list.length - 1 ? 0.3 : 0.8
                  }}
                >
                  <ArrowDown size={12} />
                </button>
                <button
                  onClick={() => handleDuplicate(idx)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', opacity: 0.8 }}
                >
                  <Copy size={12} />
                </button>
                <button
                  onClick={() => handleDelete(idx)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--status-danger)', cursor: 'pointer', opacity: 0.8 }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>

            {/* Expanded Content Fields */}
            {isExpanded && (
              <div style={{ padding: '14px 16px', borderTop: '1px solid var(--divider)', background: 'var(--surface-0)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {meta.fields?.map((field) => (
                  <FieldFactory
                    key={field.name}
                    field={field}
                    value={item[field.name]}
                    onChange={(newVal) => handleFieldChange(idx, field.name, newVal)}
                  />
                ))}

                {/* Vault attachments for supported section types */}
                {showVault && (
                  <VaultAttachPanel
                    refs={item.vault_refs || []}
                    onUpdate={(newRefs) => handleFieldChange(idx, 'vault_refs', newRefs)}
                    authToken={authToken}
                    theme={theme}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}

      <button
        onClick={handleAddItem}
        style={{
          padding: '10px', fontSize: 11.5, fontWeight: 700,
          background: 'var(--surface-1)', border: '1px dashed var(--border-default)',
          color: 'var(--brand)', borderRadius: 8, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifySelf: 'center', justifyContent: 'center', gap: 6,
          marginTop: 4
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-light)'}
        onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-1)'}
      >
        <Plus size={13} /> {`Add ${meta.title || 'Entry'}`}
      </button>
    </div>
  );
}
